import React, { useState, useEffect, useRef } from 'react';
import projects from './projectsData';

const getDomain = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/* ── PROJECT MODAL ── */
function ProjectModal({ project, onClose }) {
  // track which gallery image is active
  const [imgIndex, setImgIndex] = useState(0);

  // build image array — use project.images if available, else fall back to image_md then image
  const images = project.images?.length
    ? project.images
    : project.image_md
    ? [project.image_md]
    : [project.image];

  const prevImg = () => setImgIndex(i => (i === 0 ? images.length - 1 : i - 1));
  const nextImg = () => setImgIndex(i => (i === images.length - 1 ? 0 : i + 1));

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      // left/right arrow key navigation
      if (e.key === "ArrowLeft") prevImg();
      if (e.key === "ArrowRight") nextImg();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // reset image index when a new project opens
  useEffect(() => { setImgIndex(0); }, [project]);

  // preload every gallery image up front so arrow-key navigation is instant
  useEffect(() => {
    images.forEach(src => { const img = new Image(); img.src = src; });
  }, [images]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
        animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: "680px",
          background: "#110c1f",
          border: "1px solid rgba(124,58,237,0.3)",
          borderRadius: "5px",
          overflow: "hidden",
          boxShadow: "0 40px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(124,58,237,0.1)",
          animation: "slideUp 0.25s cubic-bezier(0.4,0,0.2,1)",
          position: "relative",
          // allow modal to scroll on small screens
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >

        {/* Image gallery replaces the old gradient banner */}
        <div style={{
          position: "relative",
          background: "#08040f",
          overflow: "hidden",
        }}>
          {/* Image */}
          <img
            key={imgIndex}
            src={images[imgIndex]}
            alt={`${project.title} screenshot ${imgIndex + 1}`}
            style={{
              width: "100%",
              aspectRatio: "34 / 15",
              objectFit: "cover",
              objectPosition: (() => {
                const custom = {
                  "freshfinder_home.png": "left",
                  "freshfinder_home_grid.png": "left",
                  "freshfinder_scale1.png": "left",
                  "freshfinder_scale2.png": "left",
                  "freshfinder_scale_mobile.png": "100% 3%",
                };
                const match = Object.keys(custom).find(name => images[imgIndex].includes(name));
                return match ? custom[match] : "top";
              })(),
              display: "block",
              animation: "fadeIn 0.25s ease",
            }}
          />

          {/* Prev arrow — only show if more than 1 image */}
          {images.length > 1 && (
            <button
              onClick={prevImg}
              style={{
                position: "absolute", left: "12px", top: "50%",
                transform: "translateY(-50%)",
                width: "36px", height: "36px",
                background: "rgba(45,212,191,1)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "50%",
                color: "#000", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
                zIndex: 2,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,0,0,0.6)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(45,212,191,1)"; e.currentTarget.style.color = "#000"; }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          )}

          {/* Next arrow */}
          {images.length > 1 && (
            <button
              onClick={nextImg}
              style={{
                position: "absolute", right: "12px", top: "50%",
                transform: "translateY(-50%)",
                width: "36px", height: "36px",
                background: "rgba(45,212,191,1)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "50%",
                color: "#000", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
                zIndex: 2,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,0,0,0.6)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(45,212,191,1)"; e.currentTarget.style.color = "#000"; }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          )}

          {/* Close button — stays top right */}
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: "12px", right: "12px",
              width: "36px", height: "36px",
              background: "rgba(0,0,0,0.6)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "50%",
              color: "#fff", fontSize: "1rem",
              cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
              zIndex: 3,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(45,212,191,1)"; e.currentTarget.style.color = "#000"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,0,0,0.6)"; e.currentTarget.style.color = "#fff"; }}
          >✕</button>


          {/* ✅ NEW: Dot indicators */}
          {images.length > 1 && (
            <div style={{
              position: "absolute", bottom: "12px", left: "50%",
              transform: "translateX(-50%)",
              display: "flex", gap: "6px",
              zIndex: 2,
            }}>
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setImgIndex(i)}
                  style={{
                    width: imgIndex === i ? "20px" : "8px",
                    height: "8px",
                    borderRadius: "100px",
                    background: imgIndex === i ? "rgb(45,212,191)" : "rgba(255,255,255,0.3)",
                    border: "none", cursor: "pointer",
                    padding: 0,
                    transition: "all 0.25s ease",
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content — unchanged from your version */}
        <div style={{ padding: "32px" }}>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
            {project.tags.map(t => (
              <span key={t} className="tag">{t}</span>
            ))}
          </div>

          <h2 style={{
            fontWeight: 800, fontSize: "1.8rem",
            color: "#f0eafa", marginBottom: "6px",
          }}>{project.title}</h2>

          <p style={{
            fontSize: "0.8rem", color: "#f472b6",
            letterSpacing: "0.08em", textTransform: "uppercase",
            fontWeight: 600, marginBottom: "20px",
          }}>{project.role}</p>

          <div style={{
            height: "1px",
            background: "linear-gradient(90deg, rgba(124,58,237,0.4), transparent)",
            marginBottom: "20px",
          }} />

          <p style={{
            fontFamily: "'Montserrat', sans-serif",
            color: "rgb(198 210 233)", fontSize: "0.95rem",
            lineHeight: 1.8, marginBottom: "32px",
          }}>{project.longDescription}</p>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              onClick={onClose}
              style={{
                background: "#000",
                color: "#fff",
                border: "1px solid #000",
                padding: "12px 24px", borderRadius: "5px",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 500, fontSize: "0.9rem",
                cursor: "pointer", transition: "all 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = "#000";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "#000";
                e.currentTarget.style.color = "#fff";
              }}
            >Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── WORK SECTION ── */
function Work() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const active = projects[selectedIndex];
  const previewRef = useRef(null);

  const selectProject = (i) => {
    setSelectedIndex(i);
    previewRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <section id="work" style={{ padding: "20px 24px 30px", position: "relative" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <h2 className="text-4xl font-bold text-center mb-12 font-bold mb-4 text-pink-400">My Work</h2>
          </div>

          <div className="work-layout">
            <div className="work-sidebar">
              {projects.map((p, i) => (
                <div
                  key={i}
                  className={`work-row${i === selectedIndex ? " active" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectProject(i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectProject(i);
                    }
                  }}
                >
                  <span className="work-row-domain">{getDomain(p.link)}</span>
                  <span className="work-row-title">{p.title}</span>
                </div>
              ))}
            </div>

            <div className="work-preview" ref={previewRef}>
              <div className="work-card-bar">
                <div className="work-card-dots">
                  <span className="work-card-dot" style={{ color: "#ff5f57" }} />
                  <span className="work-card-dot" style={{ color: "#febc2e" }} />
                  <span className="work-card-dot" style={{ color: "#28c840" }} />
                </div>
                <span className="work-card-domain">{getDomain(active.link)}</span>
              </div>
              <img
                key={active.image_md}
                src={active.image_md}
                alt={active.title}
                style={{
                  width: "100%",
                  aspectRatio: "35 / 18",
                  objectFit: "cover",
                  objectPosition: active.image_md.includes("freshfinder_home_grid.png") ? "left" : "top",
                  display: "block",
                  animation: "fadeIn 0.25s ease",
                }}
              />
              <div style={{ padding: "28px" }}>
                <h3 style={{
                  fontWeight: 700, fontSize: "1.3rem",
                  color: "#f0eafa", marginBottom: "6px",
                }}>{active.title}</h3>
                <p style={{
                  fontSize: "0.78rem", color: "#f472b6",
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  fontWeight: 600, marginBottom: "14px",
                }}>{active.role}</p>
                <p style={{
                  color: "rgb(198 210 233)", fontSize: "0.95rem", lineHeight: 1.6, marginBottom: "16px",
                }}>{active.description}</p>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px" }}>
                  {active.tags.map(t => <span key={t} className="tag">{t}</span>)}
                </div>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <button
                    className="cta-primary bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold hover:from-teal-400 hover:to-teal-500 transition-all duration-300"
                    style={{ fontSize: "0.9rem" }}
                    onClick={() => setSelectedProject(active)}
                  >
                    View project
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </>
  );
}

export default Work;